import { useEffect, useState, type FormEvent } from 'react'
import type { FiltrosDeChamado } from '../../../api/filtrosDeChamado'
import { useCompetenciasComDado } from '../../../api/queries/chamados'
import { Botao } from '../../../components/Botao'
import { CampoDeSelecao, CampoDeTexto } from '../../../components/campos'
import {
  ROTULO_ORIGEM_CHAMADO,
  ROTULO_ORIGEM_DEMANDA,
  ROTULO_SITUACAO_SLA,
  TERMOS,
  TEXTOS,
} from '../../../lib/glossario'
import type { OrigemDaDemanda, OrigemDoChamado, SituacaoDeSla } from '../../../types/chamado'
import { SeletorDeCompetencia } from '../SeletorDeCompetencia'
import { GrupoDeCaixas, type OpcaoDeFiltro } from './GrupoDeCaixas'

interface Props {
  filtros: FiltrosDeChamado
  alterar: (mudanca: Partial<FiltrosDeChamado>) => void
}

const SITUACOES: OpcaoDeFiltro[] = Object.entries(ROTULO_SITUACAO_SLA).map(([valor, rotulo]) => ({ valor, rotulo }))
const ORIGENS_DA_DEMANDA: OpcaoDeFiltro[] = Object.entries(ROTULO_ORIGEM_DEMANDA).map(([valor, rotulo]) => ({
  valor,
  rotulo,
}))

/**
 * As duas procedências ficam à vista mesmo enquanto só uma tem dado.
 *
 * Hoje tudo veio da planilha da fiscalização — o adaptador de leitura do e-Niterói ainda não foi
 * escrito. Esconder a segunda caixa até ela ter dado economizaria uma linha e cobraria caro depois:
 * no dia em que as duas fontes convivam, quem quiser separá-las vai procurar o filtro onde ele
 * sempre esteve. E, marcada, ela responde uma pergunta legítima de hoje — "existe alguma coisa aqui
 * que não veio da planilha?" — com uma lista vazia, que é a resposta certa.
 */
const PROCEDENCIAS: OpcaoDeFiltro[] = Object.entries(ROTULO_ORIGEM_CHAMADO).map(([valor, rotulo]) => ({
  valor,
  rotulo,
}))

/**
 * Os campos do painel de filtros.
 *
 * As caixas e o mês aplicam na hora; os campos de texto e as datas só ao enviar. É a diferença
 * entre uma escolha (um clique, resultado imediato) e uma digitação (uma consulta por tecla, que
 * castiga o banco e faz a tabela tremer enquanto se escreve).
 *
 * O período de abertura está aqui mesmo sem ter sido pedido na lista de filtros, e por um motivo
 * concreto: ele existe na URL porque é o recorte do contrato ("os chamados desde 06/05/2026"), e um
 * critério que a URL sabe escrever mas o painel não sabe mostrar é um recorte invisível — quem
 * abrisse um link com `?de=2026-05-06` veria o contador dizer "1 filtro" e não teria onde mexer.
 *
 * @param props critérios correntes e a função que os altera
 * @returns o formulário de filtros
 */
export function CamposDeFiltro({ filtros, alterar }: Props) {
  const { data: competencias, isPending } = useCompetenciasComDado()
  const [grupo, setGrupo] = useState(filtros.grupo)
  const [entidade, setEntidade] = useState(filtros.entidade)
  const [busca, setBusca] = useState(filtros.q)
  const [de, setDe] = useState(filtros.de)
  const [ate, setAte] = useState(filtros.ate)

  // Voltar pelo histórico do navegador muda a URL sem passar por este formulário; sem isto, os
  // campos digitados continuariam mostrando o que foi escrito antes.
  useEffect(() => setGrupo(filtros.grupo), [filtros.grupo])
  useEffect(() => setEntidade(filtros.entidade), [filtros.entidade])
  useEffect(() => setBusca(filtros.q), [filtros.q])
  useEffect(() => setDe(filtros.de), [filtros.de])
  useEffect(() => setAte(filtros.ate), [filtros.ate])

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    alterar({ grupo, entidade, q: busca, de, ate })
  }

  return (
    <form onSubmit={enviar} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <SeletorDeCompetencia
          competencias={competencias ?? []}
          valor={filtros.competencia}
          aoEscolher={(competencia) => alterar({ competencia })}
          rotuloDeTodas="Todos os meses"
          carregando={isPending}
        />
        <CampoDeTexto
          rotulo="Buscar"
          ajuda="Procura no número do chamado e no assunto."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Grupo atendente"
          ajuda="Parte do nome do grupo, como ele aparece no e-Niterói."
          value={grupo}
          onChange={(e) => setGrupo(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Entidade"
          ajuda="Parte do nome da secretaria ou do órgão que abriu o chamado."
          value={entidade}
          onChange={(e) => setEntidade(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Aberto a partir de"
          ajuda="Filtra pela data de ABERTURA — que é outro recorte, não o mês de referência."
          type="date"
          value={de}
          onChange={(e) => setDe(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Aberto até"
          ajuda="Também pela data de abertura. O contrato começou em 06/05/2026."
          type="date"
          value={ate}
          onChange={(e) => setAte(e.target.value)}
        />
      </div>

      <GrupoDeCaixas
        legenda={`Situação do ${TERMOS.sla.rotulo.toLowerCase()}`}
        ajuda={TERMOS.semPrazoDeSla.ajuda}
        opcoes={SITUACOES}
        selecionados={filtros.situacaoSla}
        aoAlternar={(valor, marcado) =>
          alterar({ situacaoSla: alternar(filtros.situacaoSla, valor, marcado) as SituacaoDeSla[] })
        }
      />

      <GrupoDeCaixas
        legenda={TERMOS.origemDaDemanda.rotulo}
        ajuda={TERMOS.origemDaDemanda.ajuda}
        opcoes={ORIGENS_DA_DEMANDA}
        selecionados={filtros.origemDemanda}
        aoAlternar={(valor, marcado) =>
          alterar({ origemDemanda: alternar(filtros.origemDemanda, valor, marcado) as OrigemDaDemanda[] })
        }
      />

      <GrupoDeCaixas
        legenda={TERMOS.procedenciaDoChamado.rotulo}
        ajuda={TERMOS.procedenciaDoChamado.ajuda}
        opcoes={PROCEDENCIAS}
        selecionados={filtros.origem}
        aoAlternar={(valor, marcado) =>
          alterar({ origem: alternar(filtros.origem, valor, marcado) as OrigemDoChamado[] })
        }
      />

      <CampoDeSelecao
        rotulo={TEXTOS.alertasDaIa.rotulo}
        ajuda={TEXTOS.alertasDaIa.ajuda}
        value={filtros.alertaDaIa}
        onChange={(evento) => alterar({ alertaDaIa: evento.target.value })}
      >
        <option value="">{TEXTOS.alertasDaIa.todos}</option>
        {Object.entries(TEXTOS.alertasDaIa.codigos).map(([codigo, rotulo]) => (
          <option key={codigo} value={codigo}>
            {String(rotulo)}
          </option>
        ))}
      </CampoDeSelecao>

      <Botao type="submit" aparencia="primario">
        Aplicar
      </Botao>
    </form>
  )
}

function alternar(atuais: readonly string[], valor: string, marcado: boolean): string[] {
  return marcado ? [...atuais, valor] : atuais.filter((v) => v !== valor)
}
